import { mstCommon } from '@libs/domain';
import type { PageProps } from '@ps-smart-cp/ppfw-client';
import {
  GridD,
  getNameWithDefault,
  MasterMaintenanceFooterButtonGroup,
  MasterMaintenanceGridButtonGroup,
  Template,
  useDeleteDialog,
  useGridD,
  useGuideTab,
  useLocalizedContents,
  useTry,
  useValidator,
} from '@ps-smart-cp/ppfw-client';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { OutputOps, OutputOrderField, SearchBasicInfo } from '../components';
import { useMstDelete, useMstListSearch } from '../hooks';
import { outputOpsFieldConfig } from '../item-config';
import type { MstListSearch, MstRegistration } from '../models';
import { OutputOperations, OutputOrder } from '../models';
import type { DeleteFunction, ExportFunction, ListRequestFunction } from '../repositories';
import type { BaseMstSearchTmplProps } from './MstTmpl.type';

interface MstMaintenanceTmplProps<S extends MstListSearch, R extends MstRegistration>
  extends BaseMstSearchTmplProps<S, R> {
  exportFunction: ExportFunction<S>;
  deleteFunction: DeleteFunction;
}

export function MstMaintenanceTmpl<S extends MstListSearch, R extends MstRegistration>({
  mstName,
  items,
  parsers,
  options,
  columns,
  listRequestFunction,
  initMst,
  deleteFunction,
  exportFunction,
  children,
  rerender,
}: MstMaintenanceTmplProps<S, R> & PageProps) {
  const navigate = useNavigate();
  const { locale } = useLocalizedContents();
  const { submitNewJob } = useGuideTab();
  const { confirm } = useDeleteDialog();

  const { search, ...gridParams } = useGridD<R, ListRequestFunction<MstListSearch, R>, number>(
    {
      rowsPerPage: 15,
      getGridSchema: listRequestFunction as ListRequestFunction<MstListSearch, R>,
      initialCondition: [initMst(), OutputOrder.init()],
    },
    { fetchDataOnMount: false },
  );
  const validator = useValidator();
  const { errorFields, validateThenTryRequest } = useTry();
  const mstSearch = useMstListSearch(initMst, items, parsers, options);
  const [outputOps, setOutputOps] = useState(OutputOperations.init);
  const { doDelete } = useMstDelete(deleteFunction);
  const [outputOrder, setOutputOrder] = useState(OutputOrder.init);

  const onSearch = validateThenTryRequest(
    () => validator.with(mstSearch.mstSearch, items).execute(),
    async () => await search(mstSearch.mstSearch, outputOrder),
  );
  const onExport = validateThenTryRequest(
    () => validator.with(mstSearch.mstSearch, items).with(outputOps, outputOpsFieldConfig).execute(),
    async () => submitNewJob((await exportFunction(mstSearch.mstSearch, outputOrder, outputOps)).jobNo),
  );

  const onChange = () => navigate(`modify/${[...gridParams.selectedRows][0]}`);

  const onDuplicate = () => {
    const row = gridParams.gridSchema.rows.find((row) => row.id === [...gridParams.selectedRows][0]);
    if (!row) return;

    navigate('register', { state: { mst: { ...row, code: '' } } });
  };

  const onDelete = async () => {
    if (!(await confirm())) return;

    await doDelete(gridParams.gridSchema.rows.filter(({ id }) => gridParams.selectedRows.has(id)));
    await search(mstSearch.mstSearch, outputOrder);
  };

  return (
    <Template
      title={
        getNameWithDefault(mstName.displayName, locale) +
        getNameWithDefault(mstCommon.titleNames.maintenance.displayName, locale)
      }
      footerButtonGroup={
        <MasterMaintenanceFooterButtonGroup
          onNew={() => navigate('register')}
          onChange={onChange}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          deleteDisabled={gridParams.selectedRows.size !== 1}
          changeDisabled={gridParams.selectedRows.size !== 1}
          duplicateDisabled={gridParams.selectedRows.size !== 1}
        />
      }
    >
      <SearchBasicInfo {...mstSearch} errorFields={errorFields}>
        {children}
      </SearchBasicInfo>
      <OutputOrderField outputKey={outputOrder} setOutputKey={setOutputOrder} />
      <OutputOps outputOperations={outputOps} setOutputOperations={setOutputOps} errorFields={errorFields} />
      <div style={{ height: '30px' }}>
        <MasterMaintenanceGridButtonGroup
          onSearch={onSearch}
          onImport={() => navigate('import')}
          onExport={onExport}
          onReset={rerender}
        />
      </div>
      <GridD columns={columns} {...gridParams} nonRowsFallback="message" rowKeyGetter={(r) => r.id} />
    </Template>
  );
}
